import { split } from 'pinyin-split';

import Language from 'types/LanguageEnum';
import { MayBe } from 'types/common';
import { Dictionaries } from 'models/Dictionaries';
import { DICTIONARY_PATHS } from 'constants/DictionaryConstants';
import { DictionaryLanguage, DictionaryName } from 'types/Dictionary';
import { uniq } from 'utils/objectUtils';
import { CandidateProvider } from 'components/Keyboard/modules/candidates';

import type { ChineseDictionary, ChineseEntry } from '../types';

const getDictionaryName = (language: DictionaryLanguage): DictionaryName => {
  if (language === Language.ZH_CN) {
    return DictionaryName.PINYIN_SIMPLIFIED;
  }
  return DictionaryName.PINYIN_TRADITIONAL;
};

const getDictionary = (dictionaryName: DictionaryName): MayBe<ChineseDictionary> => {
  const dictionary = Dictionaries.get(dictionaryName) as MayBe<ChineseDictionary>;

  if (!dictionary) {
    console.warn(`[ChineseIME] Pinyin dict not loaded — deploy ${DICTIONARY_PATHS[dictionaryName]} to ASSETS_URL`);
  }
  return dictionary;
};

const sortedChars = (entries: ChineseEntry[] = []): string[] =>
  entries.toSorted(([, scoreA], [, scoreB]) => scoreB - scoreA).map(([char]) => char);

// Returns candidates for the exact typed input.
export const lookupExact = (dictionary: ChineseDictionary, input: string): string[] => sortedChars(dictionary[input]);

// Returns candidates from keys that START WITH input but are longer (extensions).
// e.g. input="nihao" → finds "nihaoma", "nihaoa" but NOT "nihao" itself.
export const lookupExtensions = (dictionary: ChineseDictionary, input: string): string[] => {
  const matchingEntries = Object.keys(dictionary).flatMap((key) =>
    key.length > input.length && key.startsWith(input) ? dictionary[key] : [],
  );
  return uniq(sortedChars(matchingEntries));
};

// Splits input into syllables and returns candidates for each syllable,
// all merged and sorted by score (no cartesian product).
export const lookupBySegments = (dictionary: ChineseDictionary, input: string): string[] => {
  const segments = split(input);
  const entries = segments.flatMap((seg) => dictionary[seg] ?? []);
  return uniq(sortedChars(entries));
};

export const getPinyinCandidates:CandidateProvider = async (input, _, keyboard) => {
  const options = keyboard?.getOptions();
  const language = options.language;

  const dictionaryName = getDictionaryName(language);
  const dictionary = getDictionary(dictionaryName);

  if (!dictionary || !input) {
    return [];
  }

  return uniq([
    ...lookupExact(dictionary, input),
    ...lookupExtensions(dictionary, input),
    ...lookupBySegments(dictionary, input),
  ]);
};

export const formatPinyinDisplay = (composing: string): string => {
  return split(composing, true).join(' ');
};
